import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { ServerStackIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../providers/ToastProvider';
import { formatDateTime } from '../lib/format';
import { Button } from '../components/ui/button';

interface Offer {
  code: string;
  name: string;
  price_ht: number;
  price_ttc?: number;
  vcores?: number;
  ram_gb?: number;
  ssd_gb?: number;
  bandwidth?: string;
  price_monthly?: number;
  auto_deploy?: boolean;
  price_per_hour_ht?: number;
  price_per_hour_ttc?: number;
}
interface VpsItem {
  id: string;
  offer_code?: string;
  offer_name?: string;
  location?: string;
  ip_address?: string;
  os?: string;
  os_family?: string;
  os_version?: string;
  os_app?: string;
  os_mode?: string;
  protocol?: string;
  port?: number;
  vcores?: number;
  ram_gb?: number;
  ssd_gb?: number;
  price_ht?: number;
  price_ttc?: number;
  premium_backup?: boolean;
  snapshot_backup?: boolean;
  extra_storage_units?: number;
  username?: string;
  password?: string;
  auto_deploy?: boolean;
  deployment_id?: number;
  // Free-text user/admin label (e.g. "VPS001")
  label?: string;
  created_at?: string;
}

interface Deployment {
  id: number;
  vps_id: number;
  status: string;
  logs: string;
  vm_name: string;
  error_msg?: string;
  created_at?: string;
}

const TVA_RATE = 0.2;

const LOCATIONS: { [key: string]: string } = { fr: 'Europe (France)', uk: 'Europe (UK)', pl: 'Europe (Poland)' };

const OS_FAMILIES: { family: string; label: string; versions: string[]; surcharge?: number; note?: string }[] = [
  { family: 'debian', label: 'Debian', versions: ['13', '12', '11'] },
  { family: 'ubuntu', label: 'Ubuntu', versions: ['25.04', '24.04', '22.04'] },
  { family: 'fedora', label: 'Fedora', versions: ['43', '42'] },
  { family: 'almalinux', label: 'AlmaLinux', versions: ['10', '9', '8'] },
  { family: 'rocky', label: 'Rocky Linux', versions: ['10', '9', '8'] },
  { family: 'cloudlinux', label: 'CloudLinux', versions: ['9'] },
  { family: 'freebsd', label: 'FreeBSD', versions: ['15-ufs', '15-zfs', '14.3'] },
  { family: 'windows', label: 'Windows Server - Standard Edition', versions: ['2025', '2022', '2019', '2016'], surcharge: 8.5 / 1.2, note: '+8.50€ inc. VAT / month' },
];

const OS_APPS: { value: string; label: string }[] = [
  { value: 'debian-docker', label: 'Docker (Debian 12)' },
  { value: 'debian-n8n', label: 'n8n (Debian 12)' },
];

export default function Vps() {
  const [searchParams] = useSearchParams();
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();

  const [vpsList, setVpsList] = useState<VpsItem[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Order wizard
  const [showOrderModal, setShowOrderModal] = useState<boolean>(false);
  const [orderStep, setOrderStep] = useState<number>(0);
  const [selectedOfferCode, setSelectedOfferCode] = useState<string>('');
  const [locationChoice, setLocationChoice] = useState<string>('');
  const [osMode, setOsMode] = useState<'os-only' | 'os-app'>('os-only');
  const [osChoice, setOsChoice] = useState<{ family: string; version: string }>({ family: '', version: '' });
  const [osAppChoice, setOsAppChoice] = useState<string>('');
  const [premiumBackup, setPremiumBackup] = useState<boolean>(false);
  const [snapshotBackup, setSnapshotBackup] = useState<boolean>(false);
  const [extraStorageUnits, setExtraStorageUnits] = useState<number>(0);
  const [orderSubmitting, setOrderSubmitting] = useState<boolean>(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  // VPS Details modal
  const [showVpsDetailsModal, setShowVpsDetailsModal] = useState<boolean>(false);
  const [selectedVpsForDetails, setSelectedVpsForDetails] = useState<VpsItem | null>(null);
  const [showCredentials, setShowCredentials] = useState<boolean>(false);
  const [labelInput, setLabelInput] = useState<string>('');
  const [savingLabel, setSavingLabel] = useState<boolean>(false);

  const openVpsDetails = (vps: VpsItem) => {
    setSelectedVpsForDetails(vps);
    setShowCredentials(false);
    setLabelInput(vps.label || '');
    setShowVpsDetailsModal(true);
  };

  const handleSaveLabel = async () => {
    if (!selectedVpsForDetails) return;
    const trimmed = labelInput.trim();
    if ((selectedVpsForDetails.label || '') === trimmed) return;
    setSavingLabel(true);
    try {
      const res = await fetch(`${API_URL}/vps/${selectedVpsForDetails.id}/label`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: trimmed }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to update label');
      }
      const updated: VpsItem = await res.json();
      setVpsList((prev) => prev.map((v) => (v.id === selectedVpsForDetails.id ? { ...v, label: updated.label } : v)));
      setSelectedVpsForDetails({ ...selectedVpsForDetails, label: updated.label });
      toast('Label updated', 'success');
    } catch (err: any) {
      toast(err.message || 'Failed to update label', 'error');
    } finally {
      setSavingLabel(false);
    }
  };

  // Destroy confirmation
  const [showDestroyConfirm, setShowDestroyConfirm] = useState<boolean>(false);
  const [destroyingVps, setDestroyingVps] = useState<boolean>(false);
  const [destroyError, setDestroyError] = useState<string | null>(null);

  // Auto-deploy modal
  const [showDeployModal, setShowDeployModal] = useState<boolean>(false);
  const [deploymentId, setDeploymentId] = useState<number | null>(null);
  const [deploymentStatus, setDeploymentStatus] = useState<string>('');
  const [deploymentLogs, setDeploymentLogs] = useState<string[]>([]);
  const [deploySubmitting, setDeploySubmitting] = useState<boolean>(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deployedVps, setDeployedVps] = useState<VpsItem | null>(null);
  const [showDeployCredentials, setShowDeployCredentials] = useState<boolean>(false);
  const [sshReady, setSshReady] = useState<boolean>(false);
  const [sshChecking, setSshChecking] = useState<boolean>(false);
  const [deployStartTime, setDeployStartTime] = useState<number | null>(null);
  const [sshCheckStartTime, setSshCheckStartTime] = useState<number | null>(null);
  const [deployElapsed, setDeployElapsed] = useState<number>(0);
  const [sshElapsed, setSshElapsed] = useState<number>(0);

  useEffect(() => { fetchData(); }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [keycloak?.token]);

  useEffect(() => {
    const vpsId = searchParams.get('vpsId');
    if (vpsId && vpsList.length > 0) {
      const found = vpsList.find((v) => String(v.id) === String(vpsId));
      if (found) { openVpsDetails(found); }
    }
  }, [searchParams, vpsList]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vpsRes, offersRes] = await Promise.all([
        fetch(`${API_URL}/vps`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
        fetch(`${API_URL}/vps/offers`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      ]);
      if (!vpsRes.ok) throw new Error('Failed to fetch VPS list');
      if (!offersRes.ok) throw new Error('Failed to fetch offers');
      const [vpsData, offersData] = await Promise.all([vpsRes.json(), offersRes.json()]);
      setVpsList(vpsData || []);
      setOffers(offersData || []);
      setError(null);
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  const selectedOffer = offers.find((o) => o.code === selectedOfferCode);
  const selectedFamily = OS_FAMILIES.find((f) => f.family === osChoice.family);

  const computePrice = () => {
    if (!selectedOffer) return null;
    const baseHT = selectedOffer.price_ht;
    const osExtraHT = osMode === 'os-only' && selectedFamily?.surcharge ? selectedFamily.surcharge : 0;
    let optionsHT = 0;
    if (premiumBackup) optionsHT += 2;
    if (snapshotBackup) optionsHT += 0.8;
    optionsHT += extraStorageUnits * 3;
    const totalHT = baseHT + osExtraHT + optionsHT;
    const totalTTC = totalHT * (1 + TVA_RATE);
    return { baseHT, osExtraHT, optionsHT, totalHT, totalTTC };
  };

  const formatPrice = (val: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);

  const getWebSSHUrl = (vps: VpsItem) => {
    if (!vps.ip_address || !vps.password) return null;
    const hostname = encodeURIComponent(vps.ip_address);
    const port = encodeURIComponent(String(vps.port || 22));
    const username = encodeURIComponent(vps.username || 'root');
    const passwordB64 = btoa(vps.password);
    const passwordEncoded = encodeURIComponent(passwordB64);
    return `https://ssh.edj-labs.com/?hostname=${hostname}&port=${port}&username=${username}&password=${passwordEncoded}`;
  };

  const checkSshReady = async (vpsId: string | number) => {
    setSshChecking(true);
    setSshReady(false);
    setSshCheckStartTime(Date.now());
    for (let i = 0; i < 120; i++) {
      try {
        const res = await fetch(`${API_URL}/vps/${vpsId}/ssh-ready`, {
          headers: { Authorization: `Bearer ${keycloak?.token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.ready) { setSshReady(true); setSshChecking(false); setSshCheckStartTime(null); return; }
        }
      } catch { /* ignore */ }
      await new Promise((r) => setTimeout(r, 2000));
    }
    setSshChecking(false);
    setSshCheckStartTime(null);
  };

  const handleDestroyVPS = async () => {
    if (!selectedVpsForDetails) return;
    setDestroyingVps(true);
    setDestroyError(null);
    try {
      const res = await fetch(`${API_URL}/vps/${selectedVpsForDetails.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to destroy VPS');
      }
      setShowDestroyConfirm(false);
      setShowVpsDetailsModal(false);
      setSelectedVpsForDetails(null);
      await fetchData();
    } catch (err: any) {
      setDestroyError(err.message);
    } finally {
      setDestroyingVps(false);
    }
  };

  // Rundeck-style progress: linear to 95% at estimated time, then logarithmic crawl to 99%
  const calcProgress = useCallback((elapsed: number, estimate: number): number => {
    if (elapsed <= estimate) return (elapsed / estimate) * 95;
    const overtime = elapsed - estimate;
    return 95 + 4 * (1 - Math.exp(-overtime / 30));
  }, []);

  const formatElapsed = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
  };

  // Timer to update elapsed counters
  useEffect(() => {
    const id = setInterval(() => {
      if (deployStartTime && (deploymentStatus === 'pending' || deploymentStatus === 'running')) {
        setDeployElapsed((Date.now() - deployStartTime) / 1000);
      }
      if (sshCheckStartTime && sshChecking) {
        setSshElapsed((Date.now() - sshCheckStartTime) / 1000);
      }
    }, 250);
    return () => clearInterval(id);
  }, [deployStartTime, sshCheckStartTime, deploymentStatus, sshChecking]);

  // Auto-start SSH check when deploy succeeds and VPS data is available
  useEffect(() => {
    if (deploymentStatus === 'success' && deployedVps?.id && deployedVps?.ip_address && !sshReady && !sshChecking) {
      checkSshReady(deployedVps.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployedVps, deploymentStatus]);

  const openOrderModal = () => { resetOrder(); setShowOrderModal(true); };
  const closeOrderModal = () => { resetOrder(); setShowOrderModal(false); };
  const resetOrder = () => { setOrderStep(0); setSelectedOfferCode(''); setLocationChoice(''); setOsMode('os-only'); setOsChoice({ family: '', version: '' }); setOsAppChoice(''); setPremiumBackup(false); setSnapshotBackup(false); setExtraStorageUnits(0); setOrderError(null); };

  const handlePlaceOrder = async () => {
    setOrderError(null);
    setOrderSubmitting(true);
    try {
      const pricing = computePrice();
      const body: any = {
        offerCode: selectedOfferCode,
        location: locationChoice,
        osMode: osMode,
        premiumBackup: premiumBackup,
        snapshotBackup: snapshotBackup,
        extraStorageUnits: extraStorageUnits,
        priceHT: pricing?.totalHT,
        priceTTC: pricing?.totalTTC,
      };
      if (osMode === 'os-only') { body.osFamily = osChoice.family; body.osVersion = osChoice.version; } else { body.osApp = osAppChoice; }
      const res = await fetch(`${API_URL}/orders/vps`, { method: 'POST', headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { const data = await res.json().catch(() => ({})); throw new Error(data.error || 'Failed to place order'); }
      closeOrderModal();
      await fetchData();
    } catch (err: any) { setOrderError(err.message); } finally { setOrderSubmitting(false); }
  };

  const handleDeployInstantVPS = async () => {
    if (!selectedOffer || !selectedOffer.auto_deploy) return;
    setDeployError(null);
    setDeploySubmitting(true);
    setDeploymentLogs([]);
    setDeploymentStatus('pending');
    setDeployStartTime(Date.now());
    setDeployElapsed(0);
    setSshReady(false);
    setSshCheckStartTime(null);
    setSshElapsed(0);
    try {
      const res = await fetch(`${API_URL}/vps/deploy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          offer_code: selectedOffer.code,
          price_ht: selectedOffer.price_ht,
          price_ttc: selectedOffer.price_ttc || selectedOffer.price_ht * 1.2,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start deployment');
      }
      const data = await res.json();
      const depId = data.deployment_id || data.deployment?.id;
      if (data.vps) setDeployedVps(data.vps);
      setDeploymentId(depId);
      closeOrderModal();
      setShowDeployModal(true);
      // Start streaming logs
      streamDeploymentLogs(depId);
    } catch (err: any) {
      setDeployError(err.message);
    } finally {
      setDeploySubmitting(false);
    }
  };

  const streamDeploymentLogs = (depId: number) => {
    const eventSource = new EventSource(`${API_URL}/vps/deployments/${depId}/logs?token=${keycloak?.token}`);

    // Fallback: use fetch-based polling since SSE through Kong may not pass auth headers
    // We'll poll the deployment status instead
    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/vps/deployments/${depId}`, {
          headers: { Authorization: `Bearer ${keycloak?.token}` },
        });
        if (!res.ok) return;
        const dep: Deployment = await res.json();
        setDeploymentStatus(dep.status);
        if (dep.logs) {
          const lines = dep.logs.split('\n').filter((l: string) => l.trim() !== '');
          setDeploymentLogs(lines);
        }
        if (dep.status === 'success' || dep.status === 'failed') {
          clearInterval(pollInterval);
          eventSource.close();
          setDeployStartTime(null);
          await fetchData();
          if (dep.status === 'success' && dep.vps_id) {
            try {
              const vpsRes = await fetch(`${API_URL}/vps`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
              if (vpsRes.ok) {
                const allVps: VpsItem[] = await vpsRes.json();
                const found = allVps.find(v => String(v.id) === String(dep.vps_id));
                if (found) {
                  setDeployedVps(found);
                }
              }
            } catch { /* ignore */ }
          }
        }
      } catch { /* ignore poll errors */ }
    }, 2000);

    eventSource.onmessage = () => {}; // keep connection alive
    eventSource.addEventListener('log', (e) => {
      setDeploymentLogs((prev) => [...prev, e.data]);
    });
    eventSource.addEventListener('status', (e) => {
      setDeploymentStatus(e.data);
      if (e.data === 'success' || e.data === 'failed') {
        setDeployStartTime(null);
        eventSource.close();
        clearInterval(pollInterval);
        fetchData();
      }
    });
    eventSource.addEventListener('logs', (e) => {
      const lines = e.data.split('\n').filter((l: string) => l.trim() !== '');
      setDeploymentLogs(lines);
    });
    eventSource.onerror = () => {
      // SSE failed (likely auth issue through Kong), rely on polling
      eventSource.close();
    };
  };

  // Auto-scroll deployment logs (keep at bottom)
  const deployLogsContainerRef = useRef<HTMLDivElement>(null);
  const scrollLogsToBottom = useCallback(() => {
    setTimeout(() => {
      if (deployLogsContainerRef.current) {
        deployLogsContainerRef.current.scrollTop = deployLogsContainerRef.current.scrollHeight;
      }
    }, 50);
  }, []);
  useEffect(() => { scrollLogsToBottom(); }, [deploymentLogs, scrollLogsToBottom]);
  useEffect(() => { scrollLogsToBottom(); }, [deploymentStatus, scrollLogsToBottom]);

  const canNextStep = () => {
    if (orderStep === 0) return !!selectedOfferCode;
    if (orderStep === 1) return !!locationChoice;
    if (orderStep === 2) { if (osMode === 'os-only') return !!osChoice.family && !!osChoice.version; return !!osAppChoice; }
    return true;
  };

  const price = computePrice();

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<ServerStackIcon />}
          title="VPS"
          description="Manage your virtual private servers."
          actions={
            <button type="button" className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm" disabled>
              Order new VPS
            </button>
          }
        />
        <div className="bg-card border border-border rounded-lg p-6 space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">Error: {error}</p></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ServerStackIcon />}
        title="VPS"
        description="Manage your virtual private servers."
        actions={
          <button type="button" onClick={openOrderModal} className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus-visible:ring-ring focus:ring-offset-2 cursor-pointer">Order new VPS</button>
        }
      />

      {/* Order Wizard Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" onClick={closeOrderModal} />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground">New VPS Order</h2>
              <button type="button" onClick={closeOrderModal} className="text-muted-foreground hover:text-muted-foreground cursor-pointer">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Step indicator */}
            <div className="flex items-center mb-8">
              {['Offer', 'Location', 'OS', 'Options'].map((label, i) => (
                <React.Fragment key={i}>
                  <div className={`flex items-center ${i <= orderStep ? 'text-primary' : 'text-muted-foreground'}`}>
                    <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium border-2 ${i < orderStep ? 'bg-primary border-primary text-white' : i === orderStep ? 'border-primary text-primary' : 'border-border text-muted-foreground'}`}>{i < orderStep ? '✓' : i + 1}</span>
                    <span className="ml-2 text-sm font-medium hidden sm:block">{label}</span>
                  </div>
                  {i < 3 && <div className={`flex-1 h-0.5 mx-4 ${i < orderStep ? 'bg-primary' : 'bg-muted'}`} />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 0: Offer */}
            {orderStep === 0 && (
              <div className="space-y-4">
                <h3 className="text-md font-medium text-foreground">Choose an Offer</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {offers.map((offer) => (
                    <div key={offer.code} onClick={() => setSelectedOfferCode(offer.code)} className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${selectedOfferCode === offer.code ? 'border-primary bg-primary/5' : 'border-border hover:border-border'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <span className="text-sm font-semibold text-foreground">{offer.name || offer.code}</span>
                          {offer.auto_deploy && <span className="ml-2 inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">Auto-deployed</span>}
                        </div>
                        {selectedOfferCode === offer.code && <span className="text-primary"><svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg></span>}
                      </div>
                      <ul className="text-xs text-muted-foreground space-y-1 mb-3">
                        {offer.vcores && <li>{offer.vcores} vCPU</li>}
                        {offer.ram_gb && <li>{offer.ram_gb} GB RAM</li>}
                        {offer.ssd_gb && <li>{offer.ssd_gb} GB SSD</li>}
                        {offer.bandwidth && <li>{offer.bandwidth}</li>}
                      </ul>
                      {offer.auto_deploy && <div className="text-xs text-success bg-success/10 rounded p-1.5 mb-2">Auto-deployed on Debian 13. No manual approval needed.</div>}
                      {offer.auto_deploy && offer.price_per_hour_ht ? (
                        <div className="text-primary font-semibold text-sm">{offer.price_per_hour_ht.toFixed(4)}€ excl. VAT / hour<div className="text-xs text-muted-foreground font-normal mt-0.5">≈ {formatPrice(offer.price_ht)} excl. VAT / month</div></div>
                      ) : (
                        <div className="text-primary font-semibold text-sm">{formatPrice(offer.price_ht)} excl. VAT / month</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Location */}
            {orderStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-md font-medium text-foreground">Choose a Location</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {Object.entries(LOCATIONS).map(([code, name]) => (
                    <div key={code} onClick={() => setLocationChoice(code)} className={`cursor-pointer rounded-lg border-2 p-4 text-center transition-all ${locationChoice === code ? 'border-primary bg-primary/5' : 'border-border hover:border-border'}`}>
                      <div className="text-xs font-medium text-foreground">{name}</div>
                      <div className="text-xs text-muted-foreground uppercase mt-1">{code}</div>
                      {locationChoice === code && <div className="mt-2 text-primary"><svg className="h-5 w-5 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg></div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: OS */}
            {orderStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-md font-medium text-foreground">Choose an Operating System</h3>
                <div className="flex border border-border rounded-lg overflow-hidden w-fit mb-4">
                  <button type="button" onClick={() => { setOsMode('os-only'); setOsChoice({ family: '', version: '' }); setOsAppChoice(''); }} className={`px-4 py-2 text-sm font-medium cursor-pointer ${osMode === 'os-only' ? 'bg-primary text-white' : 'bg-card text-foreground hover:bg-muted/40'}`}>OS Only</button>
                  <button type="button" onClick={() => { setOsMode('os-app'); setOsChoice({ family: '', version: '' }); setOsAppChoice(''); }} className={`px-4 py-2 text-sm font-medium cursor-pointer ${osMode === 'os-app' ? 'bg-primary text-white' : 'bg-card text-foreground hover:bg-muted/40'}`}>OS + App</button>
                </div>
                {osMode === 'os-only' ? (
                  <div className="space-y-3">
                    {OS_FAMILIES.map((fam) => (
                      <div key={fam.family} className={`border-2 rounded-lg overflow-hidden ${osChoice.family === fam.family ? 'border-primary' : 'border-border'}`}>
                        <div onClick={() => setOsChoice({ family: fam.family, version: '' })} className="flex items-center justify-between p-3 cursor-pointer bg-muted/30 hover:bg-muted">
                          <div className="flex items-center gap-2">
                            {osChoice.family === fam.family && <svg className="h-4 w-4 text-primary" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                            <span className="text-sm font-medium text-foreground">{fam.label}</span>
                          </div>
                          {fam.surcharge && <span className="text-xs text-warning bg-warning/10 px-2 py-0.5 rounded-full">+{formatPrice(fam.surcharge * (1 + TVA_RATE))} inc. VAT / month</span>}
                        </div>
                        {osChoice.family === fam.family && (
                          <div className="p-3 grid grid-cols-3 gap-2">
                            {fam.versions.map((ver) => (
                              <button key={ver} type="button" onClick={() => setOsChoice({ family: fam.family, version: ver })} className={`py-2 px-3 rounded-md text-sm font-medium border cursor-pointer ${osChoice.version === ver ? 'bg-primary text-white border-primary' : 'bg-card text-foreground border-border hover:bg-muted/40'}`}>{ver}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {OS_APPS.map((app) => (
                      <div key={app.value} onClick={() => setOsAppChoice(app.value)} className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${osAppChoice === app.value ? 'border-primary bg-primary/5' : 'border-border hover:border-border'}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-foreground">{app.label}</span>
                          {osAppChoice === app.value && <svg className="h-5 w-5 text-primary" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Options */}
            {orderStep === 3 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <h3 className="text-md font-medium text-foreground">Options</h3>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-4 border border-border rounded-lg opacity-60 cursor-not-allowed">
                      <div><div className="text-sm font-medium text-foreground">Premium Backup <span className="text-xs text-amber-600 ml-1">Coming soon</span></div><div className="text-xs text-muted-foreground">Automated daily off-site backups retained 7 days</div></div>
                      <div className="flex items-center gap-3"><span className="text-sm font-semibold text-primary">+{formatPrice(2)} excl. VAT/mo</span><input type="checkbox" disabled checked={premiumBackup} onChange={(e) => setPremiumBackup(e.target.checked)} className="h-4 w-4 text-primary" /></div>
                    </label>
                    <label className="flex items-center justify-between p-4 border border-border rounded-lg opacity-60 cursor-not-allowed">
                      <div><div className="text-sm font-medium text-foreground">Snapshot Backup <span className="text-xs text-amber-600 ml-1">Coming soon</span></div><div className="text-xs text-muted-foreground">Manual on-demand snapshots retained 30 days</div></div>
                      <div className="flex items-center gap-3"><span className="text-sm font-semibold text-primary">+{formatPrice(0.8)} excl. VAT/mo</span><input type="checkbox" disabled checked={snapshotBackup} onChange={(e) => setSnapshotBackup(e.target.checked)} className="h-4 w-4 text-primary" /></div>
                    </label>
                    <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                      <div><div className="text-sm font-medium text-foreground">Extra Storage</div><div className="text-xs text-muted-foreground">Add 50 GB SSD storage units</div></div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-primary">+{formatPrice(3)} excl. VAT / 50 GB</span>
                        <select value={extraStorageUnits} onChange={(e) => setExtraStorageUnits(Number(e.target.value))} className="border border-border rounded-md py-1 px-2 text-sm focus:outline-none focus:border-primary cursor-pointer">
                          {Array.from({ length: 11 }, (_, i) => <option key={i} value={i}>{i === 0 ? 'None' : `${i} × 50 GB`}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {price && (
                  <div className="lg:col-span-1">
                    <div className="bg-muted/30 border border-border rounded-lg p-4 sticky top-0">
                      <h3 className="text-md font-medium text-foreground mb-4">Price Summary</h3>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between"><dt className="text-muted-foreground">Base ({selectedOffer?.name || selectedOfferCode})</dt><dd className="font-medium">{formatPrice(price.baseHT)}</dd></div>
                        {price.osExtraHT > 0 && <div className="flex justify-between"><dt className="text-muted-foreground">Windows license</dt><dd className="font-medium">{formatPrice(price.osExtraHT)}</dd></div>}
                        {price.optionsHT > 0 && <div className="flex justify-between"><dt className="text-muted-foreground">Options</dt><dd className="font-medium">{formatPrice(price.optionsHT)}</dd></div>}
                        <div className="border-t border-border pt-2 flex justify-between"><dt className="text-muted-foreground">Total excl. VAT</dt><dd className="font-semibold">{formatPrice(price.totalHT)}</dd></div>
                        <div className="flex justify-between"><dt className="text-muted-foreground">VAT (20%)</dt><dd className="font-medium">{formatPrice(price.totalHT * TVA_RATE)}</dd></div>
                        <div className="border-t border-border pt-2 flex justify-between text-primary font-bold"><dt>Total inc. VAT / month</dt><dd>{formatPrice(price.totalTTC)}</dd></div>
                      </dl>
                    </div>
                  </div>
                )}
              </div>
            )}

            {orderError && <div className="mt-4 rounded-md bg-destructive/10 p-3"><p className="text-sm text-red-700">{orderError}</p></div>}
            {deployError && <div className="mt-4 rounded-md bg-destructive/10 p-3"><p className="text-sm text-red-700">{deployError}</p></div>}

            <div className="mt-6 flex justify-between items-center">
              <button type="button" disabled={orderStep === 0} onClick={() => setOrderStep((p) => p - 1)} className="px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/40 disabled:opacity-40 cursor-pointer">Back</button>
              {selectedOffer?.auto_deploy && orderStep === 0 ? (
                <button type="button" disabled={!canNextStep() || deploySubmitting} onClick={handleDeployInstantVPS} className="px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-50 cursor-pointer">{deploySubmitting ? 'Deploying...' : 'Deploy Now'}</button>
              ) : orderStep < 3 ? (
                <button type="button" disabled={!canNextStep()} onClick={() => setOrderStep((p) => p + 1)} className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-40 cursor-pointer">Next</button>
              ) : (
                <button type="button" disabled={orderSubmitting} onClick={handlePlaceOrder} className="px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-50 cursor-pointer">{orderSubmitting ? 'Placing order...' : 'Place Order'}</button>
              )}
            </div>
          </div>
            </div>
          </div>
        </div>
      )}

      {/* VPS List */}
      <div className="bg-card shadow rounded-lg overflow-hidden">
        <div className="w-full overflow-x-auto touch-pan-x">
        <table className="w-full min-w-[860px] divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Offer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Label</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Specs</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">IP Address</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">OS</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Protocol</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {vpsList.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-4 text-center text-sm text-muted-foreground">You do not have any VPS yet.</td></tr>
            ) : vpsList.map((vps) => (
              <tr key={vps.id} className="hover:bg-muted/40">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                  {vps.offer_name || vps.offer_code}
                  {vps.auto_deploy && <span className="ml-1.5 inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">Auto-deployed</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {vps.label ? vps.label : <span className="text-muted-foreground italic">—</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{vps.vcores} vCores · {vps.ram_gb} GB RAM · {vps.ssd_gb} GB SSD</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{vps.ip_address || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{vps.os || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{vps.protocol || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right space-x-2">
                  {vps.protocol?.toUpperCase() === 'SSH' && vps.ip_address && vps.password && (
                    <a href={getWebSSHUrl(vps)!} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      WebSSH
                    </a>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openVpsDetails(vps)}>Details</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Deployment Progress Modal */}
      {showDeployModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full relative">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-foreground">VPS Deployment</h2>
                    {deploymentStatus === 'pending' && <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">Pending</span>}
                    {deploymentStatus === 'running' && <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">Deploying...</span>}
                    {deploymentStatus === 'success' && <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">Completed</span>}
                    {deploymentStatus === 'failed' && <span className="inline-flex items-center rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive">Failed</span>}
                  </div>
                  {(deploymentStatus === 'success' || deploymentStatus === 'failed') && (
                    <button type="button" onClick={() => { setShowDeployModal(false); setDeployedVps(null); setShowDeployCredentials(false); }} className="text-muted-foreground hover:text-muted-foreground cursor-pointer">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </div>

                {(deploymentStatus === 'pending' || deploymentStatus === 'running') && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-primary">
                        {deploymentStatus === 'pending' ? 'Preparing deployment...' : 'Provisioning your VPS...'}
                      </span>
                      <span className="text-xs text-muted-foreground">{formatElapsed(deployElapsed)} / ~30s</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                      <div className="h-2.5 rounded-full bg-primary transition-all duration-200" style={{ width: `${Math.min(calcProgress(deployElapsed, 30), 99)}%` }} />
                    </div>
                  </div>
                )}

                {deploymentStatus === 'success' && (
                  <>
                    <div className="mb-4 p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
                      <p className="text-sm text-primary font-semibold">Your VPS has been deployed successfully!</p>
                      {deployedVps && (
                        <div className="border border-primary/20 rounded-lg p-3 bg-card space-y-2">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-foreground">Access Credentials</h4>
                          </div>
                          <div className="space-y-1.5 text-sm">
                            {deployedVps.ip_address && (
                              <div className="flex justify-between"><span className="font-medium text-muted-foreground">IP Address:</span><span className="font-mono text-foreground">{deployedVps.ip_address}</span></div>
                            )}
                            <div className="flex justify-between"><span className="font-medium text-muted-foreground">Username:</span><span className="font-mono text-foreground">{deployedVps.username || 'admin'}</span></div>
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-muted-foreground">Password:</span>
                              <span className="font-mono text-foreground">
                                {showDeployCredentials ? (deployedVps.password || '-') : (
                                  <button type="button" onClick={() => setShowDeployCredentials(true)} className="text-xs text-primary hover:text-primary/80 font-medium cursor-pointer">Click to reveal</button>
                                )}
                              </span>
                            </div>
                            {deployedVps.ip_address && deployedVps.username && (
                              <div className="mt-2 p-2 bg-muted rounded text-xs font-mono text-foreground">ssh {deployedVps.username}@{deployedVps.ip_address}</div>
                            )}
                            {deployedVps.protocol?.toUpperCase() === 'SSH' && deployedVps.ip_address && deployedVps.password && (
                              <div className="mt-3 space-y-2">
                                {sshChecking ? (
                                  <>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-medium text-primary">Waiting for SSH to be ready...</span>
                                      <span className="text-xs text-muted-foreground">{formatElapsed(sshElapsed)} / ~2m</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                                      <div className="h-2.5 rounded-full bg-primary transition-all duration-200" style={{ width: `${Math.min(calcProgress(sshElapsed, 120), 99)}%` }} />
                                    </div>
                                  </>
                                ) : sshReady ? (
                                  <a href={getWebSSHUrl(deployedVps)!} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 justify-center w-full rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    Open WebSSH
                                  </a>
                                ) : (
                                  <button type="button" onClick={() => checkSshReady(deployedVps.id)}
                                    className="inline-flex items-center gap-2 justify-center w-full rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 cursor-pointer">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    Connect via WebSSH
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {deploymentStatus === 'failed' && (
                  <div className="mb-4 p-3 bg-destructive/10 border border-red-200 rounded-lg">
                    <p className="text-sm text-destructive font-medium">Deployment failed. Please contact support if this persists.</p>
                  </div>
                )}

                <div ref={deployLogsContainerRef} className="bg-gray-900 rounded-lg p-4 max-h-80 overflow-y-auto font-mono text-xs">
                  {deploymentLogs.length === 0 ? (
                    <div className="text-muted-foreground flex items-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-400" />
                      Waiting for logs...
                    </div>
                  ) : deploymentLogs.map((line, i) => (
                    <div key={i} className={`${line.includes('ERROR') ? 'text-red-400' : line.includes('successfully') || line.includes('completed') ? 'text-success' : 'text-gray-300'}`}>{line}</div>
                  ))}
                </div>
              </div>
              <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {(deploymentStatus === 'success' || deploymentStatus === 'failed') && (
                  <button type="button" onClick={() => { setShowDeployModal(false); setDeployedVps(null); setShowDeployCredentials(false); }} className="w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 sm:ml-3 sm:w-auto sm:text-sm cursor-pointer">Close</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VPS Details Modal */}
      {showVpsDetailsModal && selectedVpsForDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={() => setShowVpsDetailsModal(false)} />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground" id="modal-title">{selectedVpsForDetails.offer_name || selectedVpsForDetails.offer_code}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">VPS #{selectedVpsForDetails.id}</p>
                  </div>
                  <button type="button" onClick={() => setShowVpsDetailsModal(false)} className="bg-card rounded-md text-muted-foreground hover:text-muted-foreground focus:outline-none cursor-pointer">
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Label editor */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">Label</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value)}
                        maxLength={128}
                        placeholder="e.g. VPS001"
                        className="block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary"
                      />
                      <button
                        type="button"
                        onClick={handleSaveLabel}
                        disabled={savingLabel || (selectedVpsForDetails.label || '') === labelInput.trim()}
                        className="inline-flex items-center rounded-md border border-transparent px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {savingLabel ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">A friendly name shown in your Services list. Leave empty to clear.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Specifications</span>
                      <p className="text-sm text-foreground mt-1">{selectedVpsForDetails.vcores} vCores · {selectedVpsForDetails.ram_gb} GB RAM · {selectedVpsForDetails.ssd_gb} GB SSD</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">{selectedVpsForDetails.auto_deploy ? 'Price (Hourly)' : 'Price (Monthly)'}</span>
                      <p className="text-sm text-foreground mt-1">{selectedVpsForDetails.auto_deploy && selectedVpsForDetails.price_ht ? `${(selectedVpsForDetails.price_ht / 730).toFixed(4)}€ excl. VAT / hour` : selectedVpsForDetails.price_ht ? `${formatPrice(selectedVpsForDetails.price_ht)} excl. VAT / ${formatPrice(selectedVpsForDetails.price_ttc ?? 0)} inc. VAT` : '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Location</span>
                      <p className="text-sm text-foreground mt-1">{LOCATIONS[selectedVpsForDetails.location || ''] || selectedVpsForDetails.location || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">IP Address</span>
                      <p className="text-sm text-foreground mt-1">{selectedVpsForDetails.ip_address || 'Not assigned yet'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Operating System</span>
                      <p className="text-sm text-foreground mt-1">{selectedVpsForDetails.os || 'Not configured yet'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Protocol</span>
                      <p className="text-sm text-foreground mt-1">{selectedVpsForDetails.protocol || '-'}{selectedVpsForDetails.port ? ` (TCP ${selectedVpsForDetails.port})` : ''}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Created</span>
                      <p className="text-sm text-foreground mt-1">{selectedVpsForDetails.created_at ? formatDateTime(selectedVpsForDetails.created_at) : '-'}</p>
                    </div>
                  </div>

                  {(selectedVpsForDetails.premium_backup || selectedVpsForDetails.snapshot_backup || (selectedVpsForDetails.extra_storage_units ?? 0) > 0) && (
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="text-sm font-semibold text-foreground mb-3">Options</h4>
                      <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                        {selectedVpsForDetails.premium_backup && <li>Automated backups (+2€ excl. VAT / month)</li>}
                        {selectedVpsForDetails.snapshot_backup && <li>Snapshot backup (+0.80€ excl. VAT / month)</li>}
                        {(selectedVpsForDetails.extra_storage_units ?? 0) > 0 && (
                          <li>Extra Storage: {(selectedVpsForDetails.extra_storage_units ?? 0) * 50} GB (+{(selectedVpsForDetails.extra_storage_units ?? 0) * 3}€ excl. VAT / month)</li>
                        )}
                      </ul>
                    </div>
                  )}

                  {selectedVpsForDetails.username ? (
                    <div className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-foreground">Access Credentials</h4>
                        <button type="button" onClick={() => setShowCredentials(!showCredentials)}
                          className="text-sm text-primary hover:text-primary/80 font-medium cursor-pointer">
                          {showCredentials ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      {showCredentials ? (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium text-foreground">Username:</span>
                            <span className="text-foreground font-mono">{selectedVpsForDetails.username}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-foreground">Password:</span>
                            <span className="text-foreground font-mono">{selectedVpsForDetails.password}</span>
                          </div>
                          {selectedVpsForDetails.protocol?.toUpperCase() === 'SSH' && selectedVpsForDetails.ip_address && selectedVpsForDetails.password && (
                            <div className="mt-3">
                              <a href={getWebSSHUrl(selectedVpsForDetails)!} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 justify-center w-full rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary shadow-sm hover:bg-primary/10">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                Open WebSSH
                              </a>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Click "Show" to reveal credentials</p>
                      )}
                    </div>
                  ) : selectedVpsForDetails.auto_deploy ? (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                        <p className="text-sm font-medium text-blue-800">Auto-deploying via Terraform</p>
                      </div>
                      <p className="text-xs text-blue-700">Your VPS is being provisioned automatically. Credentials will appear once deployment completes.</p>
                      {selectedVpsForDetails.deployment_id && (
                        <button type="button" onClick={() => { setShowVpsDetailsModal(false); setDeploymentId(selectedVpsForDetails.deployment_id!); setShowDeployModal(true); streamDeploymentLogs(selectedVpsForDetails.deployment_id!); }}
                          className="mt-2 text-sm text-blue-700 underline hover:text-blue-900 cursor-pointer">
                          View deployment logs
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg">
                      <p className="text-sm text-warning">This VPS is pending provisioning. Credentials will be available once an administrator validates your order.</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:items-center sm:justify-between">
                {selectedVpsForDetails.auto_deploy ? (
                  <button type="button" onClick={() => { setDestroyError(null); setShowDestroyConfirm(true); }}
                    className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 cursor-pointer">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Destroy VPS
                  </button>
                ) : <div />}
                <button type="button" onClick={() => setShowVpsDetailsModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring sm:ml-3 sm:w-auto sm:text-sm cursor-pointer">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Destroy Confirmation Modal */}
      {showDestroyConfirm && selectedVpsForDetails && (
        <div className="fixed inset-0 z-[60] overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" onClick={() => { if (!destroyingVps) setShowDestroyConfirm(false); }} />
            <div className="inline-block bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full relative">
              <div className="bg-card px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-destructive/10 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-foreground">Destroy VPS</h3>
                    <div className="mt-2 space-y-2">
                      <p className="text-sm text-muted-foreground">You are about to permanently destroy <span className="font-semibold text-foreground">{selectedVpsForDetails.offer_name}</span> ({selectedVpsForDetails.ip_address || 'no IP'}).</p>
                      <div className="p-3 bg-destructive/10 border border-red-200 rounded-md">
                        <p className="text-sm text-destructive font-medium">This action is irreversible. The following will be permanently deleted:</p>
                        <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                          <li>The virtual machine and all its data</li>
                          <li>All associated storage volumes (disk, cloud-init ISO)</li>
                          <li>All deployment records and logs</li>
                        </ul>
                      </div>
                      <p className="text-sm text-muted-foreground font-medium">All data stored on this VPS will be lost and cannot be recovered.</p>
                    </div>
                    {destroyError && (
                      <div className="mt-3 p-2 bg-destructive/10 border border-red-200 rounded text-sm text-destructive">{destroyError}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-muted/30 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button type="button" onClick={handleDestroyVPS} disabled={destroyingVps}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 cursor-pointer">
                  {destroyingVps ? 'Destroying...' : 'Yes, destroy this VPS'}
                </button>
                <button type="button" onClick={() => setShowDestroyConfirm(false)} disabled={destroyingVps}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 cursor-pointer">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
